// Utility functions - no Firestore imports needed

/**
 * Retry operation with exponential backoff
 * @param {Function} operation - Async operation to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<any>} - Result of the operation
 */
export const retryOperation = async (operation, maxRetries = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Check if it's a Firebase error that we should retry
      const shouldRetry =
        error.code === "unavailable" ||
        error.code === "resource-exhausted" ||
        error.code === "deadline-exceeded" ||
        (error.message && error.message.includes("network"));

      if (!shouldRetry || attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

/**
 * Simple tokenizer for search word arrays
 * @param {string} str - String to tokenize
 * @returns {string[]} - Array of unique tokens
 */
export const tokenizeWords = (str) => {
  if (!str || typeof str !== "string") return [];
  const parts = str
    .toLowerCase()
    .split(/[^a-z0-9]+/gi)
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
};
